import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import OtpInput from 'react18-input-otp';
import classNames from 'classnames';

import lang from '../../../lib/language';
import { ENROLL_REQUEST_STATE } from '../../../lib/models/enroll';
import { ENROLL_AUTH_REQUEST_TYPE } from '../../../lib/models/enroll-requests';
import { RootState } from '../../../lib/store';
import { enrollAuth } from '../../../lib/store/auth';
import { NullableNumber } from '../../../lib/util/types';
import { createFormControlError } from '../../../lib/util/ValidationSchemes/Validate';
import { ControlError } from '../../../lib/util/ValidationSchemes/ValidationTypes';

import InputErrorIcon from '../../../assets/img/input-error-icon.svg';
import { PhoneAutocompleteValue } from '../../../components/PhoneAutocomplete/PhoneAutocomplete.types';
import { useIsMobile } from '../../../hooks';
import EnrollmentState from '../../../store-util/EnrollmentState';
import { UI_ROUTES } from '../../../util/routes';
import Layout from '../../Enrollment/component/Layout';
import { PhoneNumberSelector } from '../Verification.constants';

import Counter from './Counter';

import styles from './VerificationPhoneOtp.module.scss';

type LocationState = {
  [PhoneNumberSelector]: PhoneAutocompleteValue;
};

const CODE_NUMBERS = 6;

const VerificationPhoneOTP = (): JSX.Element => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useIsMobile();
  const dispatch = useDispatch();

  const isAppLoaded = useSelector<RootState, boolean | undefined>((state: RootState) => (state.app.loaded));

  const phoneCodeConfirmed = useSelector<RootState, boolean | undefined>((state: RootState) => (
    state.auth.enroll.confirmedSteps.phone
  ));

  const statePhoneCode = useSelector<RootState, string | null | undefined>((state: RootState) => (
    state.auth.enroll.phoneCode
  ));

  const statePhoneNumber = useSelector<RootState, string | null | undefined>((state: RootState) => (
    state.auth.enroll.phoneNumber
  ));

  const userId = useSelector<RootState, NullableNumber>((state: RootState) => (
    state.auth.enroll.userId ?? state.auth.enrollUserId ?? state.crm.individualExtendedInfo?.user_correlation_id
  ));

  const enrollStatus = useSelector<RootState, ENROLL_REQUEST_STATE | undefined>((state: RootState) => (
    state.auth.enroll.status
  ));

  const [phoneNumber, setPhoneNumber] = useState<PhoneAutocompleteValue>({ phone: '', code: '' });
  const [isSendAgain, setIsSendAgain] = useState<boolean>(false);
  const [hasMadeRequest, setHasMadeRequest] = useState<boolean>(false);
  const [inputCodes, setInputCodes] = useState<string>('');
  const [error, setError] = useState<ControlError>();
  const { form } = EnrollmentState.use();

  const formattedPhoneNumber = useMemo((): string => {
    let newPhoneNumber = phoneNumber.code.split(' ')[0];
    if (phoneNumber.phone.length) {
      newPhoneNumber += phoneNumber.phone.replace(/^.*([0-9]{2})$/, ` ${'*'.repeat(phoneNumber.phone.length - 2)}$1.`);
    }
    return newPhoneNumber;
  }, [phoneNumber]);

  useEffect(() => {
    if (!form.steps.isVerifyEmailPassed && !phoneNumber && isAppLoaded) {
      navigate(UI_ROUTES.emailConfirm, {
        replace: true,
      });
    }
  }, [form.steps.isVerifyEmailPassed, isAppLoaded]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (statePhoneCode && statePhoneNumber) {
      setPhoneNumber({ phone: statePhoneNumber, code: statePhoneCode });
    } else if (!location.state || !(location.state as LocationState)[PhoneNumberSelector]) {
      navigate(UI_ROUTES.verificationPhoneNumber);
    } else {
      setPhoneNumber((location.state as LocationState)[PhoneNumberSelector]);
    }
  }, [location.state, navigate, statePhoneCode, statePhoneNumber]);

  useEffect(() => {
    if (enrollStatus === ENROLL_REQUEST_STATE.ERROR) {
      const err = isSendAgain
        ? createFormControlError(lang.commonOnboardingPhoneVerificationCodeSendAgainErrorMessage())
        : createFormControlError(lang.commonOnboardingPhoneVerificationInvalidCodeLength());
      setError(err);
    }
  }, [enrollStatus, isSendAgain]);

  useEffect(() => {
    if (phoneCodeConfirmed) {
      EnrollmentState.setVerifyPhoneStepPassed(true);
      navigate(UI_ROUTES.enrollSetupUserName);
    }
  }, [hasMadeRequest, navigate, phoneCodeConfirmed, dispatch]);

  const content : JSX.Element = (
    <>

      <div className={styles.text}>
        <div className={styles.title}>
          {lang.commonOnboardingPhoneVerificationSMSCodeTitle()}
        </div>
        <div className={styles.subtitle}>
          {lang.commonOnboardingPhoneVerificationSMSCodeSubitle(formattedPhoneNumber)}
        </div>
      </div>
      <div className={styles.wrapperPasscodeFields}>
        <OtpInput
          value={inputCodes}
          onChange={(value) => {
            setInputCodes(value);
            setError(undefined);
          }}
          numInputs={CODE_NUMBERS}
          inputStyle={classNames(styles.field, { [styles.fieldErrorBorder]: error })}
          isInputNum
          shouldAutoFocus
        />
      </div>
      {error && (
      <div
        key={error.id}
        id={error.id.toString()}
        className={styles.error}
      >
        <img src={InputErrorIcon} className={styles.errorIcon} alt="error" />
        {inputCodes.length === 0 ? lang.commonVerificationInputFieldMandatory() : error.error}
      </div>
      )}
      <div className={styles.textsWrapper}>
        <div className={styles.resend}>
          {lang.commonOnboardingPhoneVerificationSMSCodeNotReceived()}
          {' '}
          {lang.commonOnboardingPhoneVerificationSMSCodeExpriesIn()}
          <Counter phoneNumber={phoneNumber} userId={userId} setIsSendAgain={setIsSendAgain} />
        </div>
      </div>
    </>
  );

  const handleSubmit = () :void => {
    setIsSendAgain(false);
    dispatch(enrollAuth({
      requestType: ENROLL_AUTH_REQUEST_TYPE.VERIFY_PHONE_CODE,
      code: inputCodes,
    }));
    setHasMadeRequest(true);
  };

  return (
    <Layout
      formTitle=""
      displayBackButton={!isMobile}
      displayNextButton
      nextButtonText={lang.commonContinueButton()}
      displayLoginButton={false}
      onSubmit={handleSubmit}
    >
      {content}
    </Layout>
  );
};

export default VerificationPhoneOTP;
